import Row from 'antd/es/grid/row'
import Col from 'antd/es/grid/col'
import { SyncOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { UserInfo } from '@/apis/auth/auth.def'
import { IdeyaButton } from '@/common/antd/ideya-button/ideya-button'
import IdeyaTypography from '@/common/antd/ideya-typography'
import { useFixedBreakpoint } from '@/common/hooks/use-is-mobile'
import { selectThemeStyle } from '@/features/setting/setting.slice'
import { selectUser } from '@/features/user/user.slice'
import { ROLE_CONTRIBUTOR } from '@/router/roles'
import { useAppSelector } from '@/redux/hooks'
import {
  canCreateIdeaPermissions,
  hasPermissionAndFirmCheck,
} from '@/utils/permissions'

interface IdeaListUserActionControlsProps {
  onCreateIdea: () => void
  onClearIdeaAlerts: () => void
  onReloadList: () => void
  lastUpdatedTime: Date
  style?: React.CSSProperties
}

export const IdeaListUserActionControls: React.FC<
  IdeaListUserActionControlsProps
> = ({
  onCreateIdea,
  onClearIdeaAlerts,
  onReloadList,
  lastUpdatedTime,
  style,
}) => {
  const { t } = useTranslation()
  const themeStyle = useAppSelector(selectThemeStyle)
  const screens = useFixedBreakpoint()
  const userInfo: UserInfo = useAppSelector(selectUser)
  const isContributor = userInfo.groupRole === ROLE_CONTRIBUTOR

  const canCreateIdea = hasPermissionAndFirmCheck(
    canCreateIdeaPermissions,
    userInfo.firmId,
    userInfo.firmRole,
    userInfo.groupRole,
    userInfo.role
  )

  return (
    <Row
      justify={'space-between'}
      wrap={false}
      style={{
        height: '2.5rem',
        borderRadius: '.5rem',
        border: themeStyle.myTitleBorder,
        padding: '0.5rem 0.75rem ',
        background: themeStyle.myTitleBg,
        ...style,
      }}
    >
      <Col>
        <IdeyaTypography.Text size={'base'}>
          {t('idea_list.idea_list')}
        </IdeyaTypography.Text>
      </Col>
      <Col>
        <Row align={'middle'} style={{ gap: '0.625rem' }}>
          <Col>
            {!screens.lg ? (
              <IdeyaTypography.Text size={'base'}>
                {lastUpdatedTime.toLocaleString('sv-SE', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                  hour12: false,
                })}
              </IdeyaTypography.Text>
            ) : (
              <IdeyaTypography.Text size={'base'}>
                {t('idea_list.latest_updated_time')} :{' '}
                {lastUpdatedTime.toLocaleString()}
              </IdeyaTypography.Text>
            )}
          </Col>
          <Col>
            <SyncOutlined
              style={{ cursor: 'pointer' }}
              onClick={onReloadList}
            />
          </Col>
          {canCreateIdea && screens.md && (
            <>
              <Col>
                <IdeyaButton
                  ideyaType={'secondary'}
                  onClick={onClearIdeaAlerts}
                  size={'small'}
                >
                  {isContributor
                    ? t('idea_list.user_controls.clear_all_alerts')
                    : t('idea_list.user_controls.clear_all_my_alerts')}
                </IdeyaButton>
              </Col>
              <Col>
                <IdeyaButton onClick={onCreateIdea} size={'small'}>
                  {t('common.create_idea')}
                </IdeyaButton>
              </Col>
            </>
          )}
        </Row>
      </Col>
    </Row>
  )
}
